// react and js
import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { useParams, useLocation, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

// i18n
import {
  isSupportedLanguage,
  ensureLanguageLoaded,
  DEFAULT_LANGUAGE,
  SUPPORTED_LANGUAGES,
} from "./index";

// ----------------------------------------------------------------------
// LanguageGate
// ----------------------------------------------------------------------
// Wraps the public layout so that:
//   * /distribution            renders in English (no :lang param)
//   * /de/distribution         renders in German, kicks off the de bundle
//   * /random/...              redirects to /     (unknown lang code)
//   * /en/...                  redirects to /...  (English needs no prefix)
//   * /ZH-cn/...               redirects to /zh-CN/... (canonical casing)
//
// The component is a passthrough — it renders { children } once the language
// state is settled. Children render with English fallbacks while a non-bundled
// locale loads, then re-render once i18n.changeLanguage() resolves.
// ----------------------------------------------------------------------
function canonicalize(code) {
  if (!code) return null;
  const lower = String(code).toLowerCase();
  const found = SUPPORTED_LANGUAGES.find(
    (l) => l.code.toLowerCase() === lower
  );
  return found ? found.code : null;
}

function stripFirstSegment(pathname) {
  const segments = pathname.split("/").filter(Boolean);
  if (segments.length <= 1) return "/";
  return `/${segments.slice(1).join("/")}`;
}

export default function LanguageGate({ children }) {
  const { lang } = useParams();
  const location = useLocation();
  const { i18n } = useTranslation();

  const matchedCanonical = isSupportedLanguage(lang) ? canonicalize(lang) : null;
  const hasCasingDrift =
    matchedCanonical !== null && matchedCanonical !== lang;

  const isExplicitEnglish =
    Boolean(lang) && matchedCanonical === DEFAULT_LANGUAGE;
  const isInvalidLang = Boolean(lang) && matchedCanonical === null;

  const targetLang =
    matchedCanonical && matchedCanonical !== DEFAULT_LANGUAGE
      ? matchedCanonical
      : DEFAULT_LANGUAGE;

  useEffect(() => {
    if (isInvalidLang || isExplicitEnglish || hasCasingDrift) return undefined;
    let cancelled = false;
    ensureLanguageLoaded(targetLang).then((loaded) => {
      if (cancelled) return;
      if (i18n.language !== loaded) i18n.changeLanguage(loaded);
    });
    return () => {
      cancelled = true;
    };
  }, [targetLang, isInvalidLang, isExplicitEnglish, hasCasingDrift, i18n]);

  if (isInvalidLang) {
    return <Navigate to="/" replace />;
  }

  if (isExplicitEnglish) {
    const rest = stripFirstSegment(location.pathname);
    const search = location.search || "";
    return <Navigate to={`${rest}${search}`} replace />;
  }

  if (hasCasingDrift) {
    const rest = stripFirstSegment(location.pathname);
    const search = location.search || "";
    const dest = rest === "/"
      ? `/${matchedCanonical}`
      : `/${matchedCanonical}${rest}`;
    return <Navigate to={`${dest}${search}`} replace />;
  }

  return children;
}

LanguageGate.propTypes = {
  children: PropTypes.node.isRequired,
};
